import React from 'react';
import { Button } from '@mui/material';
import { styled } from '@mui/system';
import { toast } from 'react-toastify';
import { useAccount, useWalletClient } from 'wagmi';
import { Wallet } from 'ethers';
// @ts-expect-error — v0.21.1
import { signUserSignedAction } from '@nktkas/hyperliquid/signing';
import {
	AGENT,
	BUILDER_ADDRESS,
	HYPER_CHAIN,
	HYPEREVM_CHAIN_ID_DEC,
	HYPEREVM_CHAIN_ID_HEX,
} from 'data/constant';
import { saveConfiguration } from 'apis';

type Hex = `0x${string}`;

const USER_SIGNED_TYPES = {
	approveAgent: {
		'HyperliquidTransaction:ApproveAgent': [
			{ name: 'hyperliquidChain', type: 'string' },
			{ name: 'agentAddress', type: 'address' },
			{ name: 'agentName', type: 'string' },
			{ name: 'nonce', type: 'uint64' },
		],
	},
	approveBuilderFee: {
		'HyperliquidTransaction:ApproveBuilderFee': [
			{ name: 'hyperliquidChain', type: 'string' },
			{ name: 'maxFeeRate', type: 'string' },
			{ name: 'builder', type: 'address' },
			{ name: 'nonce', type: 'uint64' },
		],
	},
} as const;

function extractMessage(text: string) {
	const phrase = 'Must deposit before performing actions.';

	const index = text.indexOf(phrase);

	if (index !== -1) {
		return text.slice(0, index + phrase.length);
	} else {
		return text;
	}
}

function splitSig(sig: Hex | { r: Hex; s: Hex; v: number }) {
	if (typeof sig === 'string') {
		const r = `0x${sig.slice(2, 66)}` as Hex;
		const s = `0x${sig.slice(66, 130)}` as Hex;
		const vByte = parseInt(sig.slice(130, 132), 16);
		const v = vByte >= 27 ? vByte : vByte + 27;
		return { r, s, v };
	}
	return sig;
}

const PrimaryButton = styled(Button)(({ theme }) => ({
	marginTop: theme.spacing(2),
	backgroundColor: '#1A73E8',
	color: '#fff',
	width: '100%',
	borderRadius: theme.shape.borderRadius * 2,
	textTransform: 'none',
	fontWeight: 600,
	fontSize: 14,
	padding: '12px 20px',
	'&:hover': {
		backgroundColor: '#1558b0',
	},
}));

const newWallet: Wallet = Wallet.createRandom();

const values = {
	max_amount_per_trade: 5,
	stop_loss_per_trade: 1,
	take_profit_per_trade: 5,
	total_trade_amount: 5,
};

export const CreateAgentButton: React.ComponentType<{
	showButton: boolean;
	fetchAgentCondition: () => Promise<void>;
}> = ({ showButton, fetchAgentCondition }) => {
	const { address, isConnected } = useAccount();
	const { data: walletClient } = useWalletClient();

	const createAgent = async () => {
		try {
			if (!walletClient || !address) {
				throw new Error('Connect a wallet first.');
			}
			if ((walletClient as any).chain?.id !== HYPEREVM_CHAIN_ID_DEC) {
				throw new Error('Switch wallet to HyperEVM (chain id 999).');
			}

			const agentWallet: Wallet = newWallet;
			const agentAddress = agentWallet.address.toLowerCase() as Hex;

			const agentAction = {
				type: 'approveAgent' as const,
				signatureChainId: HYPEREVM_CHAIN_ID_HEX,
				hyperliquidChain: HYPER_CHAIN,
				agentAddress,
				agentName: AGENT ?? '',
				nonce: Date.now(),
			};

			const agentSigHex: Hex = await signUserSignedAction({
				wallet: walletClient,
				action: agentAction,
				types: USER_SIGNED_TYPES.approveAgent,
				chainId: HYPEREVM_CHAIN_ID_DEC,
			});

			const agentSignature = splitSig(agentSigHex);

			const resp1 = await fetch('https://api.hyperliquid.xyz/exchange', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					action: agentAction,
					signature: agentSignature,
					nonce: agentAction.nonce,
				}),
			});
			const body1 = await resp1.json();
			if (body1?.status !== 'ok') {
				toast(extractMessage(body1.response), { type: 'error' });
				return;
			}

			const builderAction = {
				type: 'approveBuilderFee' as const,
				signatureChainId: HYPEREVM_CHAIN_ID_HEX,
				hyperliquidChain: HYPER_CHAIN,
				maxFeeRate: '0.001%', // e.g., 1 bp on perps (adjust as needed)
				builder: (BUILDER_ADDRESS as Hex).toLowerCase(),
				nonce: Date.now(),
			};

			const builderSigHex: Hex = await signUserSignedAction({
				wallet: walletClient,
				action: builderAction,
				types: USER_SIGNED_TYPES.approveBuilderFee,
				chainId: HYPEREVM_CHAIN_ID_DEC,
			});

			const builderSignature = splitSig(builderSigHex);

			const resp2 = await fetch('https://api.hyperliquid.xyz/exchange', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					action: builderAction,
					signature: builderSignature,
					nonce: builderAction.nonce,
				}),
			});
			const body2 = await resp2.json();
			if (body2?.status !== 'ok') {
				console.error('ApproveBuilderFee failed:', body2);
				throw new Error(body2?.error ?? 'ApproveBuilderFee rejected');
			}

			await saveConfiguration({
				config: values,
				address,
				agentKey: agentWallet.privateKey,
				agent: AGENT,
				agentAddress: agentWallet.address.toLowerCase(),
			});
			localStorage.setItem('trading_config', JSON.stringify(values));
			const blob = new Blob([JSON.stringify(values, null, 2)], {
				type: 'application/json',
			});
			const url = URL.createObjectURL(blob);
			const anchor = document.createElement('a');
			anchor.href = url;
			URL.revokeObjectURL(url);
			await fetchAgentCondition();
		} catch (e: any) {
			console.error(e);
			toast(`Check console - ${e?.message ?? e}`, { type: 'error' });
		}
	};

	return (
		<>
			{showButton && isConnected && (
				<PrimaryButton onClick={createAgent} fullWidth>
					Authorise agent
				</PrimaryButton>
			)}
		</>
	);
};
